import { graphql } from '@libs/graphql-types';

import { apolloClient } from 'shared/api/graphql';
import { createOrUpdateApolloCacheQueryEdges } from 'shared/utils/apollo';
import { accountImageLibraryQuery, businessImageLibraryQuery } from './queries';
import { ImageLibraryNode } from './fragments';

const GetOrCreateDefaultAccountImageLibraryMutation = graphql(/* GraphQL */ `
  mutation GetOrCreateDefaultAccountImageLibrary(
    $input: GetOrCreateDefaultAccountImageLibraryMutationInput!
  ) {
    getOrCreateDefaultAccountImageLibrary(input: $input) {
      imageLibrary {
        ...ImageLibraryFragment
      }
    }
  }
`);

/**
 * Mutation gets/creates a default AccountImageLibrary + ImageLibrary for the given account
 *
 * @param {string} accountGUID  GUID of the account to get/create a image library for
 */
export async function getOrCreateDefaultAccountImageLibrary(
  accountGUID: string,
): Promise<ImageLibraryNode | null> {
  const mutationResult = await apolloClient.mutate({
    mutation: GetOrCreateDefaultAccountImageLibraryMutation,
    variables: {
      input: {
        accountGuid: accountGUID,
      },
    },
    update: (cache, { data }) => {
      const createdOrUpdatedImageLibrary =
        data?.getOrCreateDefaultAccountImageLibrary?.imageLibrary;

      if (!createdOrUpdatedImageLibrary) {
        return;
      }

      // Patch the newly created image library into the apollo cache entry for the account's image libraries
      createOrUpdateApolloCacheQueryEdges(cache, createdOrUpdatedImageLibrary, {
        query: accountImageLibraryQuery,
        variables: {
          accountGUID,
        },
        edgesDotPath: 'accountByGuid.imageLibraries.edges',
        edgeTypeName: 'ImageLibraryNodeEdge',
      });
    },
  });

  return mutationResult.data?.getOrCreateDefaultAccountImageLibrary?.imageLibrary ?? null;
}

const GET_OR_CREATE_DEFAULT_BUSINESS_IMAGE_LIBRARY_MUTATION = graphql(/* GraphQL */ `
  mutation GetOrCreateDefaultBusinessImageLibrary(
    $input: GetOrCreateDefaultBusinessImageLibraryMutationInput!
  ) {
    getOrCreateDefaultBusinessImageLibrary(input: $input) {
      imageLibrary {
        ...ImageLibraryFragment
      }
    }
  }
`);

/**
 * Mutation gets/creates a default BusinessImageLibrary + ImageLibrary for the given business
 *
 * @param {string} businessGUID   GUID of the business to get/create a image library for
 */
export async function getOrCreateDefaultBusinessImageLibrary(businessGUID: string) {
  const mutationResult = await apolloClient.mutate({
    mutation: GET_OR_CREATE_DEFAULT_BUSINESS_IMAGE_LIBRARY_MUTATION,
    variables: {
      input: {
        businessGuid: businessGUID,
      },
    },
    update: (cache, { data }) => {
      const createdOrUpdatedImageLibrary =
        data?.getOrCreateDefaultBusinessImageLibrary?.imageLibrary;

      if (!createdOrUpdatedImageLibrary) {
        return;
      }

      // Patch the newly created image library into the apollo cache entry for the business' image libraries
      createOrUpdateApolloCacheQueryEdges(cache, createdOrUpdatedImageLibrary, {
        query: businessImageLibraryQuery,
        variables: {
          businessGUID,
        },
        edgesDotPath: 'businessByGuid.imageLibraries.edges',
        edgeTypeName: 'ImageLibraryNodeEdge',
      });
    },
  });

  return mutationResult.data?.getOrCreateDefaultBusinessImageLibrary;
}

const ADD_IMAGE_TO_IMAGE_LIBRARY_MUTATION = graphql(/* GraphQL */ `
  mutation AddImageToImageLibraryMutation($input: AddImageToImageLibraryMutationInput!) {
    addImageToImageLibrary(input: $input) {
      createdImageLibraryImage {
        ...ImageLibraryImageFragment
      }
    }
  }
`);

export type ImageLibraryImageType =
  | 'library_image'
  | 'business_image'
  | 'logo_image'
  | 'stock_image';

export type ImageLibraryImageSource = 'shutterstock' | 'user_upload';

/**
 * Mutation adds an image to an image library
 *
 * @param {string} imageLibrarySlug        Unique slug identifying the ImageLibrary which we want to update
 * @param {Object} imageData
 * @param {string} imageData.imageFileName  Name/s3 url of the image file to add to the library
 * @param {string} imageData.width          The width of the image
 * @param {string} imageData.height         The height of the image
 */
export const addImageToImageLibrary = async (
  imageLibrarySlug: string,
  {
    imageFileName,
    width,
    height,
    imageType,
    imageSource,
    stockAssetID,
    stockSearchID,
    displayName,
  }: {
    imageFileName: string;
    width: number;
    height: number;
    imageType: ImageLibraryImageType;
    imageSource: ImageLibraryImageSource;
    stockAssetID?: string;
    stockSearchID?: string;
    displayName?: string;
  },
) => {
  const mutationResult = await apolloClient.mutate({
    mutation: ADD_IMAGE_TO_IMAGE_LIBRARY_MUTATION,
    variables: {
      input: {
        imageLibrarySlug,
        imageFileName,
        imageWidth: width,
        imageHeight: height,
        imageType,
        source: imageSource,
        stockAssetId: stockAssetID,
        stockSearchId: stockSearchID,
        displayName,
      },
    },
    update(cache) {
      const imageLibraryCacheID = cache.identify({
        __typename: 'ImageLibraryNode',
        slug: imageLibrarySlug,
      });

      if (!imageLibraryCacheID) {
        return;
      }

      cache.evict({
        id: imageLibraryCacheID,
        fieldName: 'images',
      });
      cache.gc();
    },
  });

  return mutationResult.data?.addImageToImageLibrary ?? null;
};

const UPDATE_IMAGE_LIBRARY_IMAGE_MUTATION = graphql(/* GraphQL */ `
  mutation UpdateImageLibraryImage($input: UpdateImageLibraryImageMutationInput!) {
    updateImageLibraryImage(input: $input) {
      updatedImage {
        ...ImageLibraryImageFragment
      }
    }
  }
`);

/**
 * Mutation updates an ImageLibraryImage record
 *
 * @param {string} imageLibraryImageGUID
 * @param {Object} updateVariables
 * @param {boolean} [updateVariables.shouldRemove] - Set to true to update the image to be removed from its library
 * @param {boolean} [updateVariables.shouldRestore] - Set to true to update the image to be restored to its library, if it was removed
 * @param {boolean} [updateVariables.shouldUpdateLastUsedAt] - Set to true to update the image's lastUsedAt timestamp
 * @param {string} [updateVariables.displayName] - Set a new display name on the image
 */
const updateImageLibraryImage = async (
  imageLibraryImageGUID: string,
  updateVariables: {
    shouldRemove?: boolean;
    shouldRestore?: boolean;
    displayName?: string;
  },
) => {
  const mutationResult = await apolloClient.mutate({
    mutation: UPDATE_IMAGE_LIBRARY_IMAGE_MUTATION,
    variables: {
      input: {
        guid: imageLibraryImageGUID,
        ...updateVariables,
      },
    },
  });

  return mutationResult.data?.updateImageLibraryImage ?? null;
};

/**
 * Updates an image library image to be removed from the image library
 * (it can still be found in the image restoration panel)
 *
 * @param {string} imageLibraryImageGUID
 * @deprecated Use `updateIsImageLibraryImageRemoved` instead.
 */
export const removeImageLibraryImage = async (imageLibraryImageGUID: string) =>
  updateImageLibraryImage(imageLibraryImageGUID, {
    shouldRemove: true,
  });

/**
 * Updates a removed image library image to be restored to the image library
 *
 * @param {string} imageLibraryImageGUID
 * @deprecated Use `updateIsImageLibraryImageRemoved` instead.
 */
export const restoreRemovedImageLibraryImage = async (imageLibraryImageGUID: string) =>
  updateImageLibraryImage(imageLibraryImageGUID, {
    shouldRestore: true,
  });

export const updateIsImageLibraryImageRemoved = async ({
  imageLibraryImageGUID,
  isRemoved,
  imageLibrarySlug,
}: {
  imageLibraryImageGUID: string;
  isRemoved: boolean;
  imageLibrarySlug?: string;
}) =>
  apolloClient.mutate({
    mutation: UPDATE_IMAGE_LIBRARY_IMAGE_MUTATION,
    variables: {
      input: {
        guid: imageLibraryImageGUID,
        shouldRestore: !isRemoved,
        shouldRemove: isRemoved,
      },
    },
    update(cache) {
      if (!imageLibrarySlug) {
        return;
      }

      const imageLibraryCacheID = cache.identify({
        __typename: 'ImageLibraryNode',
        slug: imageLibrarySlug,
      });

      if (imageLibraryCacheID) {
        cache.evict({
          id: imageLibraryCacheID,
          fieldName: 'images',
        });
        cache.evict({
          id: imageLibraryCacheID,
          fieldName: 'removedImages',
        });
        cache.gc();
      }
    },
  });

const UPDATE_IMAGE_LIBRARY_IMAGE_LAST_USED_AT_MUTATION = graphql(/* GraphQL */ `
  mutation UpdateImageLibraryImageLastUsedAt(
    $input: UpdateImageLibraryImageLastUsedAtMutationInput!
  ) {
    updateImageLibraryImageLastUsedAt(input: $input) {
      updatedImage {
        ...ImageLibraryImageFragment
      }
    }
  }
`);

/**
 * Updates the lastUsedAt timestamp on an image library image
 *
 * @param {string} imageLibraryImageGUID
 */
export const updateImageLibraryImageLastUsedAt = async (imageLibraryImageGUID: string) => {
  const mutationResult = await apolloClient.mutate({
    mutation: UPDATE_IMAGE_LIBRARY_IMAGE_LAST_USED_AT_MUTATION,
    variables: {
      input: {
        guid: imageLibraryImageGUID,
      },
    },
  });

  return mutationResult.data?.updateImageLibraryImageLastUsedAt ?? null;
};
